/*
 * Australia Unseen – step-by-step print configurator.
 * Markup and data come from snippets/print-configurator.liquid.
 */
if (!customElements.get('print-configurator')) {
  const ORDER = ['XS', 'S', 'M', 'L', 'XL', 'EPIC'];
  const SWATCH = { white: '#f6f4f0', black: '#1f1e1c', 'raw wood': '#c9a77c' };
  const FINISHES = [
    { id: 'unframed', name: 'Print only' },
    { id: 'classic', name: 'Classic frame' },
    { id: 'shadow', name: 'ShadowBox frame' },
  ];

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const uniq = (a) => a.filter((x, i) => a.indexOf(x) === i);
  const sizeCode = (s) => String(s).trim().split(/[\s–—-]/)[0].toUpperCase();

  function parseFrame(f) {
    if (f == null || /unframed|print only/i.test(f)) return { finish: 'unframed', colour: null };
    const m = String(f).match(/^(classic|shadow\s?box)\s+(.+)$/i);
    if (!m) return { finish: 'unframed', colour: null };
    return { finish: /classic/i.test(m[1]) ? 'classic' : 'shadow', colour: m[2].trim() };
  }

  // Same placeholders as Shopify.formatMoney; drops trailing zero cents like money_without_trailing_zeros.
  function formatMoney(cents, format) {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    const type = match ? match[1] : 'amount';
    const fmt = (precision, thousands = ',', decimal = '.') => {
      const [whole, frac] = (cents / 100).toFixed(precision).split('.');
      const w = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return frac && Number(frac) !== 0 ? w + decimal + frac : w;
    };
    const value = {
      amount: () => fmt(2),
      amount_no_decimals: () => fmt(0),
      amount_with_comma_separator: () => fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(0, '.', ','),
      amount_with_apostrophe_separator: () => fmt(2, "'", '.'),
      amount_with_space_separator: () => fmt(2, ' ', ','),
      amount_no_decimals_with_space_separator: () => fmt(0, ' ', ','),
    }[type] || (() => fmt(2));
    return format.replace(placeholder, value()).replace(/<[^>]*>/g, '');
  }

  class PrintConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const D = JSON.parse(this.querySelector('[data-pc-json]').textContent);
      this.D = D;
      this.form = this.querySelector('[data-pc-form]');
      this.addBtn = this.querySelector('[data-pc-add]');
      this.bar = this.querySelector('[data-pc-bar]');

      this.V = D.variants.map((v) => {
        const p = parseFrame(D.frameIndex >= 0 ? v.options[D.frameIndex] : null);
        const sizeLabel = D.sizeIndex >= 0 ? v.options[D.sizeIndex] : '';
        return {
          id: v.id,
          price: v.price,
          compare: v.compare > v.price ? v.compare : 0,
          available: v.available,
          finish: p.finish,
          colour: p.colour,
          size: sizeCode(sizeLabel),
          frameLabel: D.frameIndex >= 0 ? v.options[D.frameIndex] : 'Print only',
          mediaId: v.mediaId,
        };
      });
      if (D.hideShadow) this.V = this.V.filter((v) => v.finish !== 'shadow');

      this.addon = {};
      if (D.addon) {
        D.addon.variants.forEach((a) => {
          if (a.available) this.addon[sizeCode(a.size)] = { id: a.id, price: a.price, compare: a.compare > a.price ? a.compare : 0 };
        });
      }

      const start = this.V.find((v) => v.id === D.current) || this.V.find((v) => v.available) || this.V[0];
      this.S = start
        ? { size: start.size, finish: start.finish, colour: start.colour, glazing: 'acrylic' }
        : { size: null, finish: 'unframed', colour: null, glazing: 'acrylic' };

      this.addEventListener('click', this.onClick.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.bar) {
        this.bar.querySelector('[data-pc-bar-add]').addEventListener('click', () => this.addToCart());
        this.setUpBar();
      }

      this.render(false);

      if (D.layout === 'compact') {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.adoptAr());
        else this.adoptAr();
      }
    }

    // Compact layout: put the Blankwall "View on your wall" button (an app block further down the column)
    // onto the main product image, like House of Spoils. Falls back to a slot under the strip.
    adoptAr() {
      const ar = document.getElementById('blankwall-container');
      const appBlock = ar && !ar.closest('.pc-overlay, [data-pc-ar]') ? ar.closest('.shopify-app-block') || ar : null;
      const wrapper = appBlock && appBlock.parentElement;

      const scope = this.closest('product-single') || document;
      const slider = scope.querySelector('.media-gallery .js-product-slider');
      if (slider) {
        const overlay = document.createElement('div');
        overlay.className = 'pc-overlay';
        if (appBlock) overlay.appendChild(appBlock);
        slider.after(overlay);
        // Lets CSS place things relative to the top of the main image (overlay sits at its bottom edge)
        const setH = () => overlay.style.setProperty('--pc-img-h', `-${slider.offsetHeight}px`);
        setH();
        if ('ResizeObserver' in window) new ResizeObserver(setH).observe(slider);
        // The sticky gallery would trap Blankwall's full-screen popup, so give it to <body>
        const modal = document.getElementById('ar-modal');
        if (modal) document.body.appendChild(modal);
      } else {
        const slot = this.querySelector('[data-pc-ar]');
        if (appBlock) slot.appendChild(appBlock);
        slot.hidden = false;
      }
      if (wrapper && wrapper.classList.contains('product-single__box__block') && !wrapper.children.length) wrapper.style.display = 'none';
    }

    /* ---------- data helpers ---------- */

    sizes() {
      return uniq(this.V.map((v) => v.size)).sort((a, b) => {
        const ia = ORDER.indexOf(a), ib = ORDER.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
    }

    colours(finish) {
      return uniq(this.V.filter((v) => v.finish === finish && v.colour).map((v) => v.colour));
    }

    find(finish, colour, size) {
      const pool = this.V.filter((v) => v.finish === finish && v.size === size);
      if (finish === 'unframed') return pool[0];
      return pool.find((v) => v.colour === colour) || pool.find((v) => v.available) || pool[0];
    }

    current() {
      return this.find(this.S.finish, this.S.colour, this.S.size);
    }

    glassAvailable() {
      return this.S.finish === 'classic' && !!this.addon[this.S.size];
    }

    glassOn() {
      return this.glassAvailable() && this.S.glazing === 'artglass';
    }

    // Price of a variant including ArtGlass when it is selected. Returns [price, compareAt or 0].
    priced(v, size) {
      let price = v.price;
      let was = v.compare || v.price;
      const ag = this.S.finish === 'classic' && v.finish === 'classic' && this.S.glazing === 'artglass' ? this.addon[size] : null;
      if (ag) {
        price += ag.price;
        was += ag.compare || ag.price;
      }
      return [price, was > price ? was : 0];
    }

    // The theme opens .js-product-popup-trigger links in its popup drawer (theme.js)
    popupAttrs(cls = '') {
      const id = this.D.t.sizeGuidePopup;
      const classes = [cls, id ? 'js-product-popup-trigger' : ''].filter(Boolean).join(' ');
      return (classes ? ` class="${classes}"` : '') + (id ? ` data-popup-id="${esc(id)}"` : '');
    }

    money(cents) {
      return esc(formatMoney(cents, this.D.moneyFormat));
    }

    priceHtml(price, was) {
      return (was ? `<s class="pc__was">${this.money(was)}</s>` : '') + `<span class="pc__price">${this.money(price)}</span>`;
    }

    dims(size, framed) {
      const s = this.D.sizes[size];
      if (!s) return '';
      return framed ? `${s.framed} framed` : s.print;
    }

    frameText(v) {
      if (!v) return '';
      if (this.D.country === 'US' && /raw wood/i.test(v.frameLabel)) return `${v.frameLabel} (Solid Ash Wood)`;
      return v.frameLabel;
    }

    /* ---------- render ---------- */

    render(fromUser = true) {
      const S = this.S;
      const D = this.D;
      const framed = S.finish !== 'unframed';

      if (framed && this.colours(S.finish).indexOf(S.colour) === -1) S.colour = this.colours(S.finish)[0];

      // Frame first: if the chosen frame isn't sold in the current size, move to the nearest size that is
      const cur = this.current();
      if (!cur || !cur.available) {
        const sizes = this.sizes();
        const at = Math.max(0, sizes.indexOf(S.size));
        const ok = sizes
          .filter((s) => { const x = this.find(S.finish, S.colour, s); return x && x.available; })
          .sort((a, b) => Math.abs(sizes.indexOf(a) - at) - Math.abs(sizes.indexOf(b) - at));
        if (ok.length) S.size = ok[0];
      }
      if (!this.glassAvailable()) S.glazing = 'acrylic';

      const v = this.current();
      if (D.layout === 'compact') {
        this.renderSteps(this.compactHtml(v, framed));
        this.renderSummary(v, framed);
        this.syncTheme(v, fromUser);
        return;
      }
      let h = '';

      // 1. Size
      h += `<section class="pc__step"><h2 class="pc__h"><span class="pc__h-key">Size</span> <span class="pc__h-q">How big should it be?</span></h2><div class="pc__list" role="group" aria-label="Size">`;
      this.sizes().forEach((s) => {
        const x = this.find(S.finish, S.colour, s);
        const ok = x && x.available;
        const on = s === S.size;
        let price = D.t.soldOut;
        if (x && ok) {
          const [p, w] = this.priced(x, s);
          price = this.priceHtml(p, w);
        }
        h += `<button type="button" class="pc__opt pc__size${on ? ' is-on' : ''}" data-set="size:${esc(s)}" aria-pressed="${on}"${ok ? '' : ' disabled'}>`
          + `<span class="pc__size-code">${esc(s)}</span>`
          + `<span class="pc__size-dims">${esc(this.dims(s, framed))}</span>`
          + `<span class="pc__prices">${price}</span></button>`;
      });
      h += `</div><p class="pc__note">${framed ? 'Sizes shown include the frame.' : 'Print sizes include the white border.'}`;
      if (D.t.sizeGuideUrl) h += ` <a href="${esc(D.t.sizeGuideUrl)}"${this.popupAttrs()}>Size guide</a>`;
      h += `</p></section>`;

      // 2. Finish
      const finishes = FINISHES.filter((f) => this.V.some((y) => y.finish === f.id));
      if (finishes.length > 1) {
        h += `<section class="pc__step"><h2 class="pc__h"><span class="pc__h-key">Finish</span> <span class="pc__h-q">Framed or print only?</span></h2><div class="pc__list" role="group" aria-label="Finish">`;
        finishes.forEach((f) => {
          const x = this.find(f.id, f.id === S.finish ? S.colour : this.colours(f.id)[0], S.size);
          const ok = x && x.available;
          const on = f.id === S.finish;
          let price = 'Not in this size';
          if (ok) price = this.priceHtml(x.price, x.compare);
          h += `<button type="button" class="pc__opt pc__card${on ? ' is-on' : ''}" data-set="finish:${f.id}" aria-pressed="${on}"${ok ? '' : ' disabled'}>`
            + `<span class="pc__card-top"><span class="pc__name">${f.name}${D.popular === f.id ? ' <span class="pc__tag">Most chosen</span>' : ''}</span><span class="pc__prices pc__prices--stack">${price}</span></span>`
            + (D.t[f.id] ? `<span class="pc__sub">${esc(D.t[f.id])}</span>` : '')
            + `</button>`;
        });
        h += `</div>`;
        if (D.t.showHelp) {
          h += `<details class="pc__help"><summary>Which finish is right for me?</summary><div class="pc__help-body">`
            + `<div><strong>Print only</strong>You already have a frame, or want to choose one locally.</div>`
            + `<div><strong>Classic</strong>A slim timber frame, ready to hang.</div>`
            + (finishes.some((f) => f.id === 'shadow') ? `<div><strong>ShadowBox</strong>A deeper frame with space between glazing and print, for a gallery look.</div>` : '')
            + `</div></details>`;
        }
        h += `</section>`;
      }

      // 3. Frame colour
      if (framed) {
        h += `<section class="pc__step"><h2 class="pc__h"><span class="pc__h-key">Frame colour</span> <span class="pc__h-q">${esc(S.colour)}</span></h2><div class="pc__swatches" role="group" aria-label="Frame colour">`;
        this.colours(S.finish).forEach((c) => {
          const x = this.V.find((y) => y.finish === S.finish && y.colour === c && y.size === S.size);
          const ok = x && x.available;
          const on = c === S.colour;
          const key = c.toLowerCase();
          const bg = key === 'raw wood' && D.rawWoodSwatch
            ? `background:${SWATCH[key]} url('${esc(D.rawWoodSwatch)}') center/cover`
            : `background:${SWATCH[key] || '#ddd'}`;
          h += `<button type="button" class="pc__sw${on ? ' is-on' : ''}" data-set="colour:${esc(c)}" aria-pressed="${on}"${ok ? '' : ' disabled'}>`
            + `<i style="${bg}"></i><span>${esc(c)}</span></button>`;
        });
        h += `</div></section>`;
      }

      // 4. Glazing (Classic frames, Australia, ArtGlass product set up)
      if (this.glassAvailable()) {
        const ag = this.addon[S.size];
        h += `<section class="pc__step"><h2 class="pc__h"><span class="pc__h-key">Glazing</span> <span class="pc__h-q">What sits in front of the print?</span></h2><div class="pc__pair" role="group" aria-label="Glazing">`
          + `<button type="button" class="pc__opt pc__card${S.glazing === 'acrylic' ? ' is-on' : ''}" data-set="glazing:acrylic" aria-pressed="${S.glazing === 'acrylic'}"><span class="pc__card-top"><span class="pc__name">UV Acrylic</span><span class="pc__prices">Included</span></span><span class="pc__sub">${esc(D.t.acrylic)}</span></button>`
          + `<button type="button" class="pc__opt pc__card${S.glazing === 'artglass' ? ' is-on' : ''}" data-set="glazing:artglass" aria-pressed="${S.glazing === 'artglass'}"><span class="pc__card-top"><span class="pc__name">ArtGlass</span><span class="pc__prices">${ag.compare ? `<s class="pc__was">+${this.money(ag.compare)}</s>` : ''}<span class="pc__price">+${this.money(ag.price)}</span></span></span><span class="pc__sub">${esc(D.t.artglass)}</span></button>`
          + `</div>`;
        if (D.t.showHelp) {
          h += `<details class="pc__help"><summary>Acrylic or ArtGlass?</summary><div class="pc__help-body pc__help-body--rows">`
            + `<div>Choose <strong>ArtGlass</strong> if the print will hang opposite a window, under downlights or in a bright room. Reflections almost disappear.</div>`
            + `<div>Choose <strong>Acrylic</strong> for most rooms, hallways and children's spaces. It is light, shatter-resistant and UV protective.</div>`
            + `</div></details>`;
        }
        h += `</section>`;
      }

      this.renderSteps(h);
      this.renderSummary(v, framed);
      this.syncTheme(v, fromUser);
    }

    // Compact layout: Frame > Frame colour (+ ArtGlass tick box) > Size, labels only, prices in the strip
    compactHtml(v, framed) {
      const S = this.S;
      const D = this.D;
      let h = '';

      const finishes = FINISHES.filter((f) => this.V.some((y) => y.finish === f.id));
      if (finishes.length > 1) {
        h += `<section class="pc__step"><div class="pc__label"><span class="pc__label-k">Frame</span></div>`
          + `<div class="pc__tiles pc__tiles--${finishes.length}" role="group" aria-label="Frame">`;
        finishes.forEach((f) => {
          const ok = this.V.some((y) => y.finish === f.id && y.available);
          const on = f.id === S.finish;
          const name = { unframed: 'Print only', classic: 'Classic', shadow: 'ShadowBox' }[f.id];
          h += `<button type="button" class="pc__opt pc__tile${on ? ' is-on' : ''}" data-set="finish:${f.id}" aria-pressed="${on}"${ok ? '' : ' disabled'}>${name}</button>`;
        });
        h += `</div>`;
        if (D.t[S.finish]) h += `<p class="pc__desc">${esc(D.t[S.finish])}</p>`;
        h += `</section>`;
      }

      if (framed) {
        h += `<section class="pc__step"><div class="pc__label"><span class="pc__label-k">Frame colour:</span> <span>${esc(S.colour)}</span></div><div class="pc__colour-row"><div class="pc__swatches" role="group" aria-label="Frame colour">`;
        this.colours(S.finish).forEach((c) => {
          const ok = this.V.some((y) => y.finish === S.finish && y.colour === c && y.available);
          const on = c === S.colour;
          const key = c.toLowerCase();
          const bg = key === 'raw wood' && D.rawWoodSwatch
            ? `background:${SWATCH[key]} url('${esc(D.rawWoodSwatch)}') center/cover`
            : `background:${SWATCH[key] || '#ddd'}`;
          h += `<button type="button" class="pc__sw${on ? ' is-on' : ''}" data-set="colour:${esc(c)}" aria-pressed="${on}" aria-label="${esc(c)}" title="${esc(c)}"${ok ? '' : ' disabled'}><i style="${bg}"></i></button>`;
        });
        h += `</div>`;
        if (this.glassAvailable()) {
          const ag = this.addon[S.size];
          const on = S.glazing === 'artglass';
          h += `<button type="button" class="pc__check${on ? ' is-on' : ''}" data-set="glazing:${on ? 'acrylic' : 'artglass'}" role="checkbox" aria-checked="${on}"><i></i>Add ArtGlass <span>+${this.money(ag.price)}</span></button>`;
        }
        h += `</div></section>`;
      }

      h += `<section class="pc__step"><div class="pc__label"><span class="pc__label-k">Size${D.t.sizeNote ? ':' : ''}</span>${D.t.sizeNote ? ` <span>${esc(D.t.sizeNote)}</span>` : ''}`
        + (D.t.sizeGuideUrl ? `<a href="${esc(D.t.sizeGuideUrl)}"${this.popupAttrs('pc__label-link')}>Size guide</a>` : '')
        + `</div><div class="pc__tiles pc__tiles--3" role="group" aria-label="Size">`;
      this.sizes().forEach((s) => {
        const x = this.find(S.finish, S.colour, s);
        const ok = x && x.available;
        const on = s === S.size;
        const dims = this.dims(s, false).replace(/\s*\(.*\)$/, '');
        h += `<button type="button" class="pc__opt pc__tile pc__tile--size${on ? ' is-on' : ''}" data-set="size:${esc(s)}" aria-pressed="${on}"${ok ? '' : ' disabled'}>`
          + `<span>${esc(s)}</span><small>${esc(dims)}</small></button>`;
      });
      h += `</div></section>`;
      return h;
    }

    renderSteps(h) {
      // Keep keyboard focus on the same option across re-renders
      const focused = document.activeElement && this.contains(document.activeElement) ? document.activeElement.getAttribute('data-set') : null;
      const openHelp = Array.from(this.querySelectorAll('.pc__help[open] summary')).map((el) => el.textContent);
      const steps = this.querySelector('[data-pc-steps]');
      steps.innerHTML = h;
      openHelp.forEach((t) => {
        const s = Array.from(steps.querySelectorAll('.pc__help summary')).find((el) => el.textContent === t);
        if (s) s.parentElement.open = true;
      });
      if (focused) {
        const el = steps.querySelector(`[data-set="${CSS.escape(focused)}"]`) || steps.querySelector(`[data-set^="${CSS.escape(focused.split(':')[0])}:"].pc__check`);
        if (el) el.focus();
      }
    }

    renderSummary(v, framed) {
      const S = this.S;
      const D = this.D;
      const lines = [];
      if (S.size) lines.push(['Size', `${S.size} · ${this.dims(S.size, framed)}`]);
      lines.push(['Finish', this.frameText(v)]);
      if (framed) lines.push(['Glazing', this.glassOn() ? 'ArtGlass' : 'UV Acrylic']);
      lines.push(['Paper', D.paper]);
      // Blankwall AR reads this (Dimensions Source Selector: #pc-ar-dimensions)
      const arDims = this.querySelector('[data-pc-ar-dims]');
      if (arDims) arDims.textContent = D.sizes[S.size] ? `Print size: ${D.sizes[S.size].print}` : '';

      const linesEl = this.querySelector('[data-pc-lines]');
      if (linesEl) linesEl.innerHTML = lines.map(([k, val]) => `<div><dt>${k}</dt><dd>${esc(val)}</dd></div>`).join('');

      const strip = this.querySelector('[data-pc-strip]');
      if (strip) {
        strip.querySelector('[data-pc-strip-size]').textContent = S.size || '';
        const edEl = strip.querySelector('[data-pc-strip-ed]');
        if (edEl && strip.dataset.pcEditions) {
          let n = 0;
          let tier = 0;
          try { n = JSON.parse(strip.dataset.pcEditions)[S.size] || 0; } catch (e) { n = 0; }
          try { tier = JSON.parse(strip.dataset.pcTiers || '{}')[S.size] || 0; } catch (e) { tier = 0; }
          edEl.textContent = n ? `Edition of ${n}` + (tier ? `\nTier ${tier}` : '') : '';
          edEl.hidden = !n;
        }
        strip.querySelector('[data-pc-strip-dims-k]').textContent = framed ? 'Framed size' : 'Print size';
        const sz = D.sizes[S.size];
        strip.querySelector('[data-pc-strip-dims]').textContent = sz ? (framed ? sz.framed : sz.print.replace(/\s*\(.*\)$/, '')) : '';
      }

      const [total, was] = v ? this.priced(v, S.size) : [null, 0];
      const totalText = total != null ? formatMoney(total, D.moneyFormat) : '';
      const wasText = was ? formatMoney(was, D.moneyFormat) : '';
      this.querySelector('[data-pc-total]').textContent = totalText;
      const wasEl = this.querySelector('[data-pc-was]');
      wasEl.hidden = !was;
      wasEl.textContent = wasText;
      const save = this.querySelector('[data-pc-save]');
      if (save) {
        save.hidden = !was;
        save.textContent = was ? `You save ${formatMoney(was - total, D.moneyFormat)}` : '';
      }

      const ship = this.querySelector('[data-pc-ship]');
      const shipText = framed ? D.t.shipFramed : D.t.shipUnframed;
      const made = this.querySelector('[data-pc-made]');
      if (made) {
        // Compact: "Printed and framed in Australia · Ready to ship in 2 weeks" on one line
        const parts = [];
        if (D.t.madeIn) parts.push(`${framed ? 'Printed and framed' : 'Printed'} in ${D.t.madeIn}`);
        if (shipText) parts.push(`Ready to ship in ${shipText}`);
        made.querySelector('[data-pc-made-text]').textContent = parts.join(' · ');
        made.hidden = !parts.length;
        ship.hidden = true;
      } else {
        ship.textContent = shipText ? `Production time to your country: ${shipText}` : '';
        ship.hidden = !shipText;
      }

      const ok = !!(v && v.available);
      this.addBtn.disabled = !ok;
      this.addBtn.closest('.js-product-buttons').classList.toggle('is-disabled', !ok);
      this.addBtn.querySelector('.js-product-add-text').textContent = !ok ? D.t.soldOut
        : D.layout === 'compact' && totalText ? `${D.t.addToCart} · ${totalText}` : D.t.addToCart;

      if (this.bar) {
        this.bar.querySelector('[data-pc-config]').textContent = [S.size, v && v.frameLabel, this.glassOn() ? 'ArtGlass' : null].filter(Boolean).join(' · ');
        this.bar.querySelector('[data-pc-bar-price]').textContent = totalText;
        const barWas = this.bar.querySelector('[data-pc-bar-was]');
        barWas.hidden = !was;
        barWas.textContent = wasText;
        const barBtn = this.bar.querySelector('[data-pc-bar-add]');
        barBtn.disabled = !ok;
        barBtn.textContent = ok ? D.t.addToCart : D.t.soldOut;
      }
    }

    // Tell the rest of the page (gallery, form, apps) about the selected variant
    syncTheme(v, fromUser) {
      if (!v) return;
      const input = this.form.querySelector('input[name="id"]');
      if (input.value !== String(v.id)) {
        input.value = v.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (fromUser) {
        const productSingle = this.closest('product-single');
        if (productSingle && v.mediaId) productSingle.variant = { id: v.id, featured_media: { id: v.mediaId } };

        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variantId: v.id } }));
    }

    /* ---------- events ---------- */

    onClick(e) {
      const b = e.target.closest('[data-set]');
      if (!b || b.disabled || !this.contains(b)) return;
      const raw = b.getAttribute('data-set');
      const i = raw.indexOf(':');
      const key = raw.slice(0, i);
      const val = raw.slice(i + 1);
      if (key === 'finish' && val !== this.S.finish) {
        // Keep the colour if the new finish has it, otherwise render() picks the first one
        this.S.finish = val;
      } else {
        this.S[key] = val;
      }
      this.render(true);
    }

    onSubmit(e) {
      e.preventDefault();
      this.addToCart();
    }

    setUpBar() {
      if (!('IntersectionObserver' in window)) return;
      const show = (on) => {
        this.bar.classList.toggle('is-visible', on);
        this.bar.setAttribute('aria-hidden', String(!on));
        this.bar.querySelector('[data-pc-bar-add]').tabIndex = on ? 0 : -1;
        document.body.classList.toggle('pc-bar-on', on);
      };
      // Show the bar once the configurator is on screen or passed, and the main button is out of view
      let mainVisible = true;
      let reached = false;
      const update = () => show(reached && !mainVisible);
      new IntersectionObserver(([entry]) => {
        mainVisible = entry.isIntersecting;
        update();
      }).observe(this.addBtn);
      new IntersectionObserver(([entry]) => {
        reached = entry.isIntersecting || entry.boundingClientRect.top < 0;
        update();
      }).observe(this.querySelector('[data-pc-steps]'));
    }

    /* ---------- cart ---------- */

    async addToCart() {
      const v = this.current();
      if (!v || !v.available || this._adding) return;
      this._adding = true;

      const items = [{ id: v.id, quantity: 1, properties: {} }];
      if (this.glassOn()) {
        const key = `ag${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        items[0].properties = { Glazing: 'ArtGlass', _ag_key: key };
        items.push({
          id: this.addon[this.S.size].id,
          quantity: 1,
          properties: { For: `${this.D.title} – ${v.frameLabel} / ${this.S.size}`, _ag_key: key },
        });
      }

      this.removeError();
      this.setAdding('adding');

      const store = window.theme && window.theme.cart && window.theme.cart.store;
      try {
        if (this.D.drawer && store && typeof window.theme.mfpOpen === 'function') {
          // The theme cart store reads properties as "properties[Name]" keys
          const flat = items.map((item) => {
            const out = { id: item.id, quantity: item.quantity };
            Object.entries(item.properties).forEach(([k, val]) => { out[`properties[${k}]`] = val; });
            return out;
          });
          await store.getState().add(flat);
          await store.getState().resetVariantsBeingAdded();
          this.setAdding('added');
          window.theme.mfpOpen('cart');
        } else {
          const res = await fetch(this.D.cartAdd, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items.map((i) => (Object.keys(i.properties).length ? i : { id: i.id, quantity: i.quantity })) }),
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
          window.location.href = this.D.cartUrl;
        }
      } catch (err) {
        this.setAdding('error');
        this.renderError(err && err.message ? err.message : 'Could not add to cart. Please try again.');
      } finally {
        this._adding = false;
      }
    }

    // Mirrors the theme's product-form button states (spinner, then tick)
    setAdding(state) {
      const btn = this.addBtn;
      btn.classList.remove('is-adding', 'is-added');
      if (state === 'adding') btn.classList.add('is-adding');
      if (state === 'added') {
        btn.classList.add('is-added');
        const done = () => btn.classList.remove('is-added');
        btn.addEventListener('animationend', done, { once: true });
        setTimeout(done, 2500);
      }
    }

    renderError(message) {
      const error = document.createElement('div');
      error.className = 'errors qty-error u-small';
      error.textContent = message;
      this.addBtn.before(error);
    }

    removeError() {
      const error = this.form.querySelector('.errors');
      if (error) error.remove();
    }
  }

  customElements.define('print-configurator', PrintConfigurator);
}
